// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: small shared Agents pieces: a copyable terminal block, the running spinner ring, the
// thread status marks (design/konvo-agents/r3-status.png) and a clock that ticks for relative
// times and countdowns.

import styles from '@app/features/konvo_agents/components/AgentBits.module.css';
import type {ThreadMark} from '@app/features/konvo_agents/state/AgentsModel';
import {useLingui} from '@lingui/react/macro';
import {
	IconCheck,
	IconChecks,
	IconCopy,
	IconExclamationMark,
	IconPlayerPause,
	IconQuestionMark,
} from '@tabler/icons-react';
import {clsx} from 'clsx';
import type React from 'react';
import {Fragment, useCallback, useEffect, useState} from 'react';

/** Re-renders every `everyMs` and returns the current time. */
export function useNow(everyMs = 30_000): number {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const timer = window.setInterval(() => setNow(Date.now()), everyMs);
		return () => window.clearInterval(timer);
	}, [everyMs]);
	return now;
}

export async function copyText(text: string): Promise<boolean> {
	try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch {
		return false;
	}
}

/**
 * Terminal lines with one copy button for all of them. `lines` are the commands (copied joined by
 * newlines); `comment` is a grey "# …" line above them that is not copied. `highlight` marks a
 * token inside the last line (the one-time code).
 */
export function CommandBlock({
	lines,
	comment,
	highlight,
	wrap = false,
	prompt = true,
	className,
	'data-flx': flx,
}: {
	lines: ReadonlyArray<string>;
	comment?: string;
	highlight?: string;
	/** Wrap long lines (e.g. the connect command with its install URL) instead of scrolling. */
	wrap?: boolean;
	/** Shell prompt decoration; never included in copied text. */
	prompt?: boolean;
	className?: string;
	'data-flx'?: string;
}) {
	const {t} = useLingui();
	const [copied, setCopied] = useState(false);
	const copy = useCallback(async () => {
		if (await copyText(lines.join('\n'))) {
			setCopied(true);
			window.setTimeout(() => setCopied(false), 1500);
		}
	}, [lines]);
	return (
		<div className={clsx(styles.block, className)} data-flx={flx ?? 'konvo-agents.command'}>
			<pre className={clsx(styles.lines, wrap && styles.wrap)}>
				{comment && <span className={styles.comment}># {comment}</span>}
				{lines.map((line, index) => (
					<span key={`${index}-${line}`} className={styles.line}>
						{prompt && <span className={styles.prompt}>$ </span>}
						{highlight && line.includes(highlight)
							? line.split(highlight).map((part, i) => (
									<Fragment key={i}>
										{i > 0 && <span className={styles.code}>{highlight}</span>}
										{part}
									</Fragment>
								))
							: line}
					</span>
				))}
			</pre>
			<button
				type="button"
				className={styles.copy}
				onClick={() => void copy()}
				aria-label={copied ? t`Copied` : t`Copy`}
				title={copied ? t`Copied` : t`Copy`}
				data-flx="konvo-agents.command.copy"
			>
				{copied ? <IconCheck size={16} stroke={2} /> : <IconCopy size={16} stroke={1.8} />}
			</button>
		</div>
	);
}

/** The ring a running thread shows. */
export function RunningRing({size = 14, className}: {size?: number; className?: string}) {
	return (
		<span
			className={clsx(styles.ring, className)}
			style={{width: size, height: size} as React.CSSProperties}
			aria-hidden="true"
		/>
	);
}

/**
 * A thread's status mark: spinner while running, ink "?" when it needs you, red "!" when it
 * failed, a red pause when it stopped on its plan limit, a green double tick once finished until opened. Once opened (and for a thread that
 * never ran) the slot stays empty so titles still line up.
 */
export function StatusMark({mark, className}: {mark: ThreadMark; className?: string}) {
	const {t} = useLingui();
	const label =
		mark === 'running'
			? t`Running`
			: mark === 'needs'
				? t`Needs you`
				: mark === 'paused'
					? t`Paused on the plan limit`
					: mark === 'error'
						? t`Failed`
						: mark === 'done'
							? t`Finished, not opened yet`
							: null;
	const content = (
		<>
			{mark === 'running' && <RunningRing size={13} />}
			{mark === 'needs' && (
				<span className={clsx(styles.markDisc, styles.markNeeds)}>
					<IconQuestionMark size={11} stroke={2.8} />
				</span>
			)}
			{mark === 'error' && (
				<span className={clsx(styles.markDisc, styles.markError)}>
					<IconExclamationMark size={11} stroke={2.8} />
				</span>
			)}
			{mark === 'paused' && <IconPlayerPause size={15} stroke={2.2} className={styles.markPaused} />}
			{mark === 'done' && <IconChecks size={18} stroke={2.1} className={styles.markDone} />}
		</>
	);
	if (!label) {
		return <span className={clsx(styles.mark, className)} data-mark={mark} aria-hidden="true" />;
	}
	return (
		<span
			className={clsx(styles.mark, className)}
			data-mark={mark}
			role="img"
			aria-label={label}
			data-flx="konvo-agents.mark"
		>
			{content}
		</span>
	);
}

/** An inline `code` span in running text. */
export function Code({children}: {children: React.ReactNode}) {
	return <code className={styles.inlineCode}>{children}</code>;
}
